import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Section";
import { RotateCw, Vibrate, Check } from "lucide-react";

const COUNT = 96;
const FRAMES = Array.from({ length: COUNT }, (_, i) => `/band360hi/frame-${String(i + 1).padStart(2, "0")}.jpg`);

// source of each data point
const SRC = {
  measured: { c: "#3ad6a0", t: "Measured on the band" },
  phone: { c: "#00e5ff", t: "Supplied by your phone" },
  wearspray: { c: "#ff2d78", t: "WearSpray estimate" },
};

const STAGES = [
  {
    word: "Sense", color: "#3ad6a0",
    callouts: [
      { l: "Wrist Sensor", v: "Reading active", s: "measured" },
      { l: "Heart Rate", v: "114 BPM", s: "measured" },
    ],
    support: "WearSpray begins with signals from the wrist, activity, heart rate and skin temperature.",
    phone: {
      title: "Active outdoors",
      rows: [
        { k: "Wrist link", v: "Live data received", s: "measured" },
        { k: "Workout", v: "Detected", s: "measured" },
        { k: "Perspiration", v: "Likely elevated", s: "wearspray" },
      ],
      primary: "Monitoring",
    },
  },
  {
    word: "Understand", color: "#ff2d78",
    callouts: [{ l: "Data Sync", v: "Wrist + environment", s: "phone" }],
    support: "The phone adds location, UV index and weather. WearSpray Sense cross references it all with wrist signals and application history.",
    phone: {
      title: "Exposure analysis",
      rows: [
        { k: "Location", v: "Nassau, Bahamas", s: "phone" },
        { k: "UV intensity", v: "9 · Very high", s: "phone" },
        { k: "Time outdoors", v: "76 minutes", s: "phone" },
        { k: "Last application", v: "94 minutes ago", s: "wearspray" },
      ],
      primary: "Analyzing",
    },
  },
  {
    word: "Prompt", color: "#ff2d78", vibrate: true,
    callouts: [{ l: "Tactile Alert", v: "Gentle vibration", s: "measured" }],
    support: "A private vibration on your wrist prompts you at the moment action may be needed.",
    phone: {
      title: "Notification",
      notif: {
        title: "Time to reapply",
        body: "Your sun protection may need refreshing.",
        btns: ["Reapply now", "Remind me in 10 minutes"],
      },
    },
  },
  {
    word: "Act", color: "#3ad6a0",
    callouts: [{ l: "Sunscreen on your wrist", v: "Ready when you are", s: "measured" }],
    support: "The recommended solution is already on the wrist. One tap confirms and logs the moment.",
    phone: {
      title: "Reapplication ready",
      rows: [
        { k: "Step 1", v: "Apply your chosen sunscreen", s: "measured" },
        { k: "Step 2", v: "Then confirm completion", s: "wearspray" },
      ],
      timer: true,
      primary: "Protected",
    },
  },
  {
    word: "Learn", color: "#b794f6",
    callouts: [{ l: "Personalized Prompts", v: "Improving over time", s: "wearspray" }],
    support: "WearSpray Sense learns your routines, responses, products and preferences over time.",
    phone: {
      title: "Your sun routine",
      rows: [
        { k: "Activity", v: "Usual outdoor pattern", s: "wearspray" },
        { k: "Product", v: "Preferred sunscreen remembered", s: "wearspray" },
        { k: "Pattern", v: "Weekend routine recognized", s: "wearspray" },
      ],
      final: "Sense. Understand. Prompt. Act. Learn.",
      primary: "Learning",
    },
  },
];

const FRAMES_PER_STAGE = COUNT / STAGES.length;
const stageOf = (frame) => Math.min(STAGES.length - 1, Math.floor(frame / FRAMES_PER_STAGE));

function SourceDot({ s }) {
  const info = SRC[s];
  return <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: info.c }} title={info.t} />;
}

function Phone({ stage }) {
  const st = STAGES[stage];
  const p = st.phone;
  return (
    <div className="mx-auto w-full max-w-[340px]">
      <div className="rounded-[2.8rem] border border-zinc-700 bg-black p-3" style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.7)" }} data-testid="sync-phone">
        <div className="relative rounded-[2.2rem] bg-[#0a0a0c] border border-zinc-800 overflow-hidden flex flex-col" style={{ aspectRatio: "1080 / 2160" }}>
          {/* status bar */}
          <div className="flex items-center justify-between px-5 pt-3.5 pb-1">
            <span className="font-mono text-[10px] text-white">2:42</span>
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3ad6a0] animate-pulse" />
              <span className="font-mono text-[8px] tracking-[0.16em] uppercase text-zinc-400">Connected</span>
            </span>
          </div>

          {/* header */}
          <div className="px-5 pt-2 pb-3 border-b border-white/5">
            <span className="font-mono text-sm tracking-[0.16em] uppercase text-white">Wear<span className="text-accent">Spray</span> <span style={{ color: "#ff8fc0" }}>Sense</span></span>
            <div className="flex items-center gap-4 mt-2 font-mono text-[9px] tracking-[0.12em] uppercase text-zinc-500">
              <span>UV <span className="text-[#ffb37e]">9</span></span>
              <span>SPF 50</span>
              <span>1h 16m out</span>
            </div>
          </div>

          {/* stage screen */}
          <div className="flex-1 px-5 py-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={stage}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                data-testid="sync-phone-screen"
              >
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase mb-4" style={{ color: st.color }}>{p.title}</p>

                {p.notif ? (
                  <div className="rounded-2xl border p-4" style={{ borderColor: `${st.color}66`, background: `${st.color}12` }}>
                    <p className="font-heading text-white text-lg font-medium leading-tight">{p.notif.title}</p>
                    <p className="font-body text-zinc-300 text-xs leading-relaxed mt-1.5">{p.notif.body}</p>
                    <button className="mt-4 w-full rounded-xl text-black font-body text-sm font-semibold py-2.5" style={{ background: st.color }}>{p.notif.btns[0]}</button>
                    <button className="mt-2 w-full rounded-xl border border-zinc-700 text-zinc-300 font-body text-xs py-2">{p.notif.btns[1]}</button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {p.rows?.map((r) => (
                      <div key={r.k} className="flex items-start justify-between gap-3">
                        <span className="flex items-center gap-2 font-body text-zinc-500 text-[11px]"><SourceDot s={r.s} />{r.k}</span>
                        <span className="font-body text-white text-[13px] text-right leading-tight">{r.v}</span>
                      </div>
                    ))}
                    {p.timer && (
                      <div className="flex justify-center pt-3">
                        <div className="relative w-20 h-20">
                          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                            <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
                            <motion.circle cx="50" cy="50" r="44" fill="none" stroke={st.color} strokeWidth="7" strokeLinecap="round" strokeDasharray="276"
                              initial={{ strokeDashoffset: 276 }} animate={{ strokeDashoffset: 70 }} transition={{ duration: 1.1, ease: "easeOut" }} />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <Check size={16} style={{ color: st.color }} />
                            <span className="font-mono text-[7px] tracking-[0.12em] uppercase text-zinc-400 mt-0.5">Refreshed</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {p.final && (
                      <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-center pt-3" style={{ color: st.color }}>{p.final}</p>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* primary action */}
          {p.primary && !p.notif && (
            <div className="px-5 pb-5">
              <button className="w-full rounded-xl text-black font-body text-sm font-semibold py-3" style={{ background: st.color }}>
                {p.secondary ? p.primary : p.primary}
              </button>
              {p.secondary && <button className="mt-2 w-full rounded-xl border border-zinc-700 text-zinc-300 font-body text-xs py-2">{p.secondary}</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SyncDemo() {
  const [frame, setFrame] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [auto, setAuto] = useState(true);
  const containerRef = useRef(null);
  const drag = useRef({ active: false, startX: 0, startFrame: 0 });
  const stage = stageOf(frame);
  const st = STAGES[stage];

  useEffect(() => {
    let done = 0;
    const imgs = FRAMES.map((src) => {
      const img = new Image();
      img.onload = img.onerror = () => { done += 1; setLoaded(done); };
      img.src = src;
      return img;
    });
    return () => imgs.forEach((i) => (i.onload = i.onerror = null));
  }, []);
  const ready = loaded >= COUNT;

  useEffect(() => {
    if (!auto || !ready) return;
    // ~5s of dwell time per stage (COUNT/STAGES.length ≈ 19.2 frames at 260ms) so each stage's callout,
    // phone screen and copy are actually readable before the next begins.
    const id = setInterval(() => setFrame((f) => (f + 1) % COUNT), 260);
    return () => clearInterval(id);
  }, [auto, ready]);

  const stop = useCallback(() => setAuto(false), []);
  const onDown = (e) => { stop(); drag.current = { active: true, startX: e.clientX, startFrame: frame }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => {
    if (!drag.current.active) return;
    const w = containerRef.current?.offsetWidth || 400;
    const delta = Math.round((e.clientX - drag.current.startX) / (w / COUNT));
    setFrame((((drag.current.startFrame - delta) % COUNT) + COUNT) % COUNT);
  };
  const onUp = (e) => { drag.current.active = false; e.currentTarget.releasePointerCapture?.(e.pointerId); };

  return (
    <section className="border-t border-zinc-800 bg-black overflow-hidden" data-testid="home-sync-demo">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <Reveal>
          <div className="inline-flex items-center gap-2.5 border border-[#ff6fae]/40 bg-[#ff6fae]/[0.07] rounded-full px-4 py-2 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#ff6fae] animate-pulse" />
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#ff8fc0]">Near-term integration</span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.05] text-white max-w-3xl">
            One system. <span className="text-accent">Band and app, in sync.</span>
          </h2>
          <p className="font-body text-zinc-400 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
            Rotate the band to walk through how the wrist, your phone and WearSpray Sense work together.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-16 items-start mt-14">
          {/* LEFT · band */}
          <Reveal>
            <div className="flex flex-col items-center">
              <div className="w-full max-w-[560px] h-[132px] md:h-[116px] flex flex-col justify-start">
                <div className="flex items-baseline gap-4 mb-2">
                  <span className="font-heading text-4xl md:text-5xl font-light tracking-tighter" style={{ color: st.color }}>{st.word}</span>
                  <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500">{stage + 1} / {STAGES.length}</span>
                </div>
                <p className="font-body text-zinc-400 text-sm leading-relaxed">{st.support}</p>
              </div>

              <div
                ref={containerRef}
                className="relative w-full max-w-[520px] aspect-[4/3] select-none touch-none cursor-grab active:cursor-grabbing mt-2 overflow-hidden"
                onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
                data-testid="sync-band-viewer"
              >
                {/* glow */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl pointer-events-none transition-colors duration-700"
                  style={{ width: "62%", height: "62%", background: `radial-gradient(circle, ${st.color}33, transparent 70%)` }} />

                {/* prompt vibration ring */}
                {st.vibrate && (
                  <motion.div key={`pulse-${stage}`} className="absolute left-1/2 top-1/2 rounded-full border-2 pointer-events-none"
                    style={{ borderColor: st.color, width: "52%", height: "52%", marginLeft: "-26%", marginTop: "-26%" }}
                    initial={{ scale: 0.7, opacity: 0.7 }} animate={{ scale: [0.7, 1.25], opacity: [0.7, 0] }}
                    transition={{ duration: 1.1, repeat: Infinity, ease: "easeOut" }} />
                )}

                {/* band (single cached frame, scaled up + centered so the wearable is large and stationary) */}
                <motion.div className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  animate={st.vibrate ? { x: [0, -2, 2, -2, 2, 0] } : { x: 0 }}
                  transition={st.vibrate ? { duration: 0.5, repeat: Infinity, repeatDelay: 0.9 } : { duration: 0.2 }}>
                  <img loading="lazy"
                    src={FRAMES[frame]}
                    alt="WearSpray band"
                    draggable={false}
                    className="w-[150%] max-w-none h-auto"
                    style={{ mixBlendMode: "screen" }}
                    data-testid="sync-band-image"
                  />
                </motion.div>

                {/* band callouts */}
                <div className="absolute right-2 top-2 space-y-2.5 max-w-[52%] z-10 flex flex-col items-end">
                  <AnimatePresence mode="popLayout">
                    {st.callouts.map((c) => (
                      <motion.div key={c.l} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.35 }} className="rounded-2xl border border-white/10 bg-black/45 backdrop-blur-md px-4 py-3 text-right">
                        <span className="flex items-center justify-end gap-1.5 font-mono text-[10px] tracking-[0.14em] uppercase" style={{ color: SRC[c.s].c }}>
                          <SourceDot s={c.s} />{c.l}
                        </span>
                        <span className="font-body text-white text-sm leading-tight block mt-1">{c.v}</span>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                {st.vibrate && (
                  <div className="absolute right-3 bottom-3 flex items-center gap-1.5 text-[#ff2d78] z-10">
                    <Vibrate size={14} className="animate-pulse" />
                    <span className="font-mono text-[9px] tracking-[0.16em] uppercase">Buzz</span>
                  </div>
                )}

                {!ready && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">Loading {Math.round((loaded / COUNT) * 100)}%</span>
                  </div>
                )}
              </div>

              {/* slider */}
              <div className="w-full max-w-[560px]">
                <input type="range" min={0} max={COUNT - 1} value={frame}
                  onChange={(e) => { stop(); setFrame(Number(e.target.value)); }}
                  className="w-full accent-[#00e5ff] cursor-pointer" aria-label="Rotate the band and step through the demo"
                  data-testid="sync-slider" />
                <div className="flex items-center gap-1 mt-3">
                  {STAGES.map((s, i) => (
                    <button key={s.word} onClick={() => { stop(); setFrame(Math.round(i * FRAMES_PER_STAGE)); }}
                      className="flex-1 h-1 rounded-full transition-all duration-300"
                      style={{ background: i === stage ? s.color : "#3f3f46" }}
                      data-testid={`sync-stage-${i}`} aria-label={s.word} />
                  ))}
                </div>
                <div className="flex items-center gap-1.5 mt-3 font-mono text-[10px] tracking-[0.14em] uppercase text-zinc-500">
                  <RotateCw size={11} className="text-accent" /> Drag the band or slider
                </div>
              </div>

              {/* legend */}
              <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mt-8">
                {Object.entries(SRC).map(([k, v]) => (
                  <span key={k} className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.12em] uppercase text-zinc-400">
                    <span className="w-2 h-2 rounded-full" style={{ background: v.c }} />{v.t}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          {/* RIGHT · phone */}
          <Reveal delay={0.1}>
            <Phone stage={stage} />
          </Reveal>
        </div>

        <p className="font-mono text-xs text-zinc-600 mt-14 max-w-3xl leading-relaxed">
          The band senses and delivers. The phone adds location, weather and UV context. WearSpray Sense cross references everything and recommends an action. Sensor set and vibration are shown as the near-term direction of the platform, not every function of the current band.
        </p>
      </div>
    </section>
  );
}
